import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Settings, Copy, Check, Plus, Trash2 } from 'lucide-react';
import { Agent, PromptWithHistory } from '@/types';
import { getAgent, getPrompts, deletePrompt } from '@/lib/api';
import { cn, formatDate, getStatusColor } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AgentSettings } from '@/components/agent/AgentSettings';
import { PromptEditor } from '@/components/prompt/PromptEditor';
import { PromptVersionHistory } from '@/components/prompt/PromptVersionHistory';
import { AgentAnalytics } from '@/components/analytics/AgentAnalytics';
import { PromptTestPanel } from '@/components/prompt/PromptTestPanel';
import { CreatePromptDialog } from '@/components/prompt/CreatePromptDialog';

export default function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [prompts, setPrompts] = useState<PromptWithHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'prompts' | 'analytics' | 'settings'>('overview');
  const [copied, setCopied] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<{ name: string; version?: string } | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState<{ name: string } | null>(null);
  const [showTestPanel, setShowTestPanel] = useState<{ name: string } | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deletingPrompt, setDeletingPrompt] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!agentId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [agentData, promptsData] = await Promise.all([
          getAgent(agentId),
          getPrompts(agentId),
        ]);
        setAgent(agentData);
        setPrompts(promptsData);
      } catch (err) {
        console.error('Failed to fetch agent:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agentId]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAgentUpdate = (updatedAgent: Agent) => {
    setAgent(updatedAgent);
  };

  const refreshPrompts = async () => {
    if (!agentId) return;
    try {
      const promptsData = await getPrompts(agentId);
      setPrompts(promptsData);
    } catch (err) {
      console.error('Failed to refresh prompts:', err);
    }
  };

  const handleDeletePrompt = async () => {
    if (!deletingPrompt || !agentId) return;
    setIsDeleting(true);
    try {
      await deletePrompt(agentId, deletingPrompt);
      setDeletingPrompt(null);
      await refreshPrompts();
    } catch (err) {
      console.error('Failed to delete prompt:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">載入中...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display font-semibold text-xl mb-2">找不到 Agent</h2>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            返回 Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="px-4 lg:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div>
              <h1 className="font-display font-semibold text-lg">{agent.displayName}</h1>
              <p className="text-xs text-muted-foreground font-mono hidden sm:block">
                {agent.agentId}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              編輯
            </Button>
            <Button
              size="sm"
              className={cn(
                agent.status === 'running'
                  ? 'bg-status-paused hover:bg-status-paused/90'
                  : 'bg-status-running hover:bg-status-running/90'
              )}
            >
              {agent.status === 'running' ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  暫停
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  啟動
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-72 border-r border-border/50 p-4 space-y-4 hidden lg:block">
          {/* Overview Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">概況</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={cn('w-2 h-2 rounded-full', getStatusColor(agent.status))} />
                <span className="text-sm capitalize">{agent.status === 'running' ? '運行中' : agent.status === 'paused' ? '暫停' : agent.status === 'draft' ? '草稿' : '已停止'}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                建立於 {formatDate(agent.createdAt)}
              </p>
              <p className="text-xs text-muted-foreground">
                最後更新 {formatDate(agent.updatedAt)}
              </p>
            </CardContent>
          </Card>

          {/* Real-time Stats */}
          {agent.stats && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">即時狀態</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-display font-bold">{agent.stats.activeUsers}</p>
                  <p className="text-xs text-muted-foreground">用戶</p>
                </div>
                <div>
                  <p className="text-2xl font-display font-bold">{agent.stats.todayMessages}</p>
                  <p className="text-xs text-muted-foreground">訊息</p>
                </div>
                <div className="col-span-2">
                  <p className="text-2xl font-display font-bold">{(agent.stats.avgResponseTime / 1000).toFixed(1)}s</p>
                  <p className="text-xs text-muted-foreground">平均回應時間</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Connection Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">連線資訊</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">WebSocket</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">
                    wss://.../agent/{agent.agentId}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleCopy(`wss://.../agent/${agent.agentId}`)}
                  >
                    {copied ? <Check className="h-3 w-3 text-status-running" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6">
          <div className="max-w-4xl">
            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-border">
              <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                概況
              </TabButton>
              <TabButton active={activeTab === 'prompts'} onClick={() => setActiveTab('prompts')}>
                Prompts ({prompts.length})
              </TabButton>
              <TabButton active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
                分析
              </TabButton>
              <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
                設定
              </TabButton>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="space-y-6 animate-fade-in">
                <Card>
                  <CardHeader>
                    <CardTitle>Agent 描述</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {agent.description || '暫無描述'}
                    </p>
                  </CardContent>
                </Card>

                {agent.tags && agent.tags.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>標籤</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {agent.tags.map((tag) => (
                          <Badge key={tag} variant="secondary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'prompts' && agentId && (
              <div className="animate-fade-in">
                {editingPrompt ? (
                  <PromptEditor
                    agentId={agentId}
                    promptName={editingPrompt.name}
                    version={editingPrompt.version}
                    onClose={() => setEditingPrompt(null)}
                    onUpdate={refreshPrompts}
                  />
                ) : showVersionHistory ? (
                  <PromptVersionHistory
                    agentId={agentId}
                    promptName={showVersionHistory.name}
                    currentVersion={prompts.find(p => p.name === showVersionHistory.name)?.currentVersion || '1.0'}
                    versions={prompts.find(p => p.name === showVersionHistory.name)?.versions || []}
                    onClose={() => setShowVersionHistory(null)}
                    onUpdate={refreshPrompts}
                  />
                ) : showTestPanel ? (
                  <PromptTestPanel
                    agentId={agentId}
                    promptName={showTestPanel.name}
                    availableVersions={prompts.find(p => p.name === showTestPanel.name)?.versions.map(v => v.version) || ['1.0']}
                    currentVersion={prompts.find(p => p.name === showTestPanel.name)?.currentVersion || '1.0'}
                    onClose={() => setShowTestPanel(null)}
                  />
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <Button onClick={() => setShowCreateDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        建立新 Prompt
                      </Button>
                    </div>
                    {prompts.map((prompt, index) => (
                      <PromptCard
                        key={prompt.name}
                        prompt={prompt}
                        index={index}
                        onEdit={() => setEditingPrompt({ name: prompt.name })}
                        onVersionHistory={() => setShowVersionHistory({ name: prompt.name })}
                        onTest={() => setShowTestPanel({ name: prompt.name })}
                        onDelete={() => setDeletingPrompt(prompt.name)}
                      />
                    ))}
                  </div>
                )}

                {/* Create Prompt Dialog */}
                {showCreateDialog && (
                  <CreatePromptDialog
                    agentId={agentId}
                    existingPromptNames={prompts.map(p => p.name)}
                    onClose={() => setShowCreateDialog(false)}
                    onCreated={(newPrompt) => {
                      refreshPrompts();
                      setEditingPrompt({ name: newPrompt.name });
                    }}
                  />
                )}

                {/* Delete Confirmation Dialog */}
                {deletingPrompt && (
                  <AlertDialog open onOpenChange={(open: boolean) => !open && setDeletingPrompt(null)}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>刪除 Prompt</AlertDialogTitle>
                        <AlertDialogDescription>
                          確定要刪除「{deletingPrompt}」嗎？此操作無法復原。
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeletingPrompt(null)}>
                          取消
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeletePrompt}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          disabled={isDeleting}
                        >
                          {isDeleting ? '刪除中...' : '確認刪除'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}

            {activeTab === 'analytics' && (
              <AgentAnalytics agent={agent} />
            )}

            {activeTab === 'settings' && (
              <AgentSettings agent={agent} onUpdate={handleAgentUpdate} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function TabButton({
  active,
  children,
  onClick
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-3 text-sm font-medium border-b-2 transition-colors",
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
      )}
    >
      {children}
    </button>
  );
}

function PromptCard({
  prompt,
  index,
  onEdit,
  onVersionHistory,
  onTest,
  onDelete
}: {
  prompt: PromptWithHistory;
  index: number;
  onEdit: () => void;
  onVersionHistory: () => void;
  onTest: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              📄 {prompt.name}
            </CardTitle>
            {prompt.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {prompt.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={prompt.currentVersion ? 'default' : 'secondary'}>
              v{prompt.currentVersion}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            編輯
          </Button>
          <Button variant="outline" size="sm" onClick={onVersionHistory}>
            版本歷史
          </Button>
          <Button variant="outline" size="sm" onClick={onTest}>
            測試
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
